/**
 * i18n (design spec §9.25, I-17, ADR 0017): react-i18next with ICU messages; `en`, `de` and `es` complete
 * at every milestone (`just i18n-check`). Language is per device (localStorage, I-7) with the OS as first
 * guess; the choice is also told to the server, so Telegram, email and buttons answer in it.
 */
import i18n from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";
import en from "../locales/en/common.json";
import de from "../locales/de/common.json";
import es from "../locales/es/common.json";

export const LANGUAGES = ["en", "de", "es"] as const;
export type Language = (typeof LANGUAGES)[number];
const KEY = "drang.device.lang";

export function deviceLanguage(): Language {
  try {
    const stored = localStorage.getItem(KEY);
    if ((LANGUAGES as readonly string[]).includes(stored ?? "")) return stored as Language;
  } catch {
    /* storage unavailable */
  }
  const os = navigator.language?.toLowerCase().slice(0, 2);
  return (LANGUAGES as readonly string[]).includes(os ?? "") ? (os as Language) : "en";
}

export function setDeviceLanguage(lang: Language): void {
  try {
    localStorage.setItem(KEY, lang);
  } catch {
    /* storage unavailable */
  }
  void i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
  tellServer(lang);
}

/** Server-side messages (Telegram, email, buttons, the calendar feed) use the person's last choice. */
export function tellServer(lang: Language): void {
  void fetch("/api/internal/session/lang", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lang }),
  }).catch(() => undefined);
}

void i18n
  .use(ICU)
  .use(initReactI18next)
  .init({
    resources: { en: { common: en }, de: { common: de }, es: { common: es } },
    lng: deviceLanguage(),
    // core catalogues are complete (`just i18n-check`); English covers a plugin that lacks a language
    fallbackLng: "en",
    defaultNS: "common",
    interpolation: { escapeValue: false },
    returnNull: false,
    saveMissing: import.meta.env.DEV,
    missingKeyHandler: (_lngs, ns, key) => {
      if (import.meta.env.DEV) console.warn(`[i18n] missing key ${ns}:${key}`);
    },
  });

document.documentElement.lang = i18n.language;

export default i18n;
